// Ads Manager deep links.
//
// One place for the URL shape: the launcher links out from the launch cards,
// the history table and the template detail, and three hand-built copies drift.
//
// The `act` parameter matters — without it Ads Manager opens whichever account
// the browser last used, and silently shows nothing for ids that are not in it.
const BASE = 'https://adsmanager.facebook.com/adsmanager/manage';

export interface AdsManagerTarget {
  campaignId?: string | null;
  adsetId?: string | null;
  adId?: string | null;
}

/**
 * A link that selects every level it is given, not just the deepest one.
 *
 * Ads Manager takes `selected_campaign_ids`, `selected_adset_ids` and
 * `selected_ad_ids` together — its own URLs do exactly that — and `treenav`
 * opens the left-hand tree so the campaign and ad set are visible around the
 * ad rather than being context you have to go and find. Passing only the ad id
 * lands you on the ad with nothing selected above it.
 *
 * The path segment follows the deepest level asked for, because that is the
 * tab Ads Manager opens on.
 */
export function adsManagerUrl(accountId: string, target: AdsManagerTarget): string {
  const { campaignId, adsetId, adId } = target;
  const params = [`act=${accountId}`];
  if (campaignId) params.push(`selected_campaign_ids=${campaignId}`);
  if (adsetId) params.push(`selected_adset_ids=${adsetId}`);
  if (adId) params.push(`selected_ad_ids=${adId}`);

  const level = adId || adsetId ? 'ads' : 'campaigns';
  // Only worth opening the tree when there is a hierarchy to show.
  if ([campaignId, adsetId, adId].filter(Boolean).length > 1) params.push('treenav=true');
  return `${BASE}/${level}?${params.join('&')}`;
}

export function campaignUrl(accountId: string, campaignId: string): string {
  return adsManagerUrl(accountId, { campaignId });
}

/** Opens the ad set's ads, which is what you want when inspecting one. */
export function adsetUrl(accountId: string, adsetId: string, campaignId?: string | null): string {
  return adsManagerUrl(accountId, { campaignId, adsetId });
}

export function adUrl(
  accountId: string,
  adId: string,
  within: Omit<AdsManagerTarget, 'adId'> = {},
): string {
  return adsManagerUrl(accountId, { ...within, adId });
}
